import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { Subscription } from "rxjs";
import moment from "moment";
import { IMetadataImage } from "../../../viewer-components/interfaces/i-metadataimage";
import { ImagesService } from "../../../viewer-components/services/internal-services/images.service";
import { IGalleryStatus } from "../../../viewer-components/interfaces/i-gallery-status";
import { VIEWER_SERVICE } from "../../../viewer-components/services/internal-services/viewer-services/inject-base-viewer.service";
import { ViewerConfigurationService } from "../../../viewer-components/services/viewer-configuration.service";
import { ImageGalleryService } from "../../../viewer-components/services/internal-services/image-gallery/image-gallery.service";

@Component({
    selector: "app-full-screen-image",
    templateUrl: "./full-screen-image.component.html",
    styleUrls: ["./full-screen-image.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class FullScreenImageComponent implements OnInit, OnDestroy {
  viewerService = inject(VIEWER_SERVICE);

  private subscriptions: Subscription[] = [];

  public status: IGalleryStatus;

  public image: IMetadataImage;

  public serialNumber: number;

  public totalAmount: number;

  public captureDate: string;

  public type: string;

  public shortName: string;

  public isButtonsShown: boolean = false;

  public isInfoShown: boolean = false;

  public isLoading: boolean = false;

  private _mapCenterCoordinates: number[] = [];

  public isArrowLabelShown: any = {
    next: false,
    prev: false,
  };

  obliqueAvailable = true;

  constructor(
    private viewerConfigurationService: ViewerConfigurationService,
    protected galleryService: ImageGalleryService,
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.galleryService.fullscreenImage$.subscribe((result) => {
        if (result) {
          this.formatImageMetadata(result);
        }
      }),
    );
    this.subscriptions.push(
      this.galleryService.fullscreenImageSerialNumber$.subscribe((result) => {
        this.serialNumber = result;
      }),
    );
    this.subscriptions.push(
      this.galleryService.status$.subscribe((status) => {
        this.status = status;
      }),
    );

    this.subscriptions.push(
      this.galleryService.obliqueAvailable$.subscribe((obliqueAvailable) => {
        this.obliqueAvailable = obliqueAvailable;
      }),
    );
    this.subscriptions.push(
      this.viewerService.mapCenterCoordinates$.subscribe((coordinates: number[]) => {
        this.mapCenterCoordinates = coordinates;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  getShortName(): string {
    let name = this.image.original_image_name.slice(0, 15);
    name += "...";
    name += this.image.original_image_name.slice(-8);

    return name;
  }

  formatImageMetadata(image: IMetadataImage) {
    this.image = image;
    this.captureDate = moment(this.image.capture_date).format("MM/DD/YYYY");
    this.type = ImagesService.getImageResolutionLabel(this.image);
    this.shortName = this.image.original_image_name.length > 20 ? this.getShortName() : this.image.original_image_name;
    this.totalAmount = this.galleryService.getTotalAmountOfFilteredImages();
    this.isLoading = true;
  }

  onHoverHandler(variable: string, isShown: boolean): void {
    variable.split(".").length === 2
      ? (this[variable.split(".")[0]][variable.split(".")[1]] = isShown)
      : (this[variable] = isShown);
  }

  closeFnc(): void {
    this.galleryService.closeFullscreen();
  }

  next(): void {
    this.galleryService.showAdjacentFullscreenImage(1);
    this.isLoading = true;
  }

  prev(): void {
    this.galleryService.showAdjacentFullscreenImage(-1);
    this.isLoading = true;
  }

  viewImageOnMap(): void {
    this.galleryService.viewImageOnMap(this.image);
    this.galleryService.setDisplayGallery(false);
  }

  forceDownload(blob, filename) {
    const a = document.createElement("a");
    a.download = filename;
    a.href = blob;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  downloadResource(url: string, filename: string) {
    fetch(url, {
      headers: new Headers({
        Origin: location.origin,
      }),
      mode: "cors",
    })
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        this.forceDownload(blobUrl, filename);
      })
      .catch((e) => console.error(e));
  }

  handleImageInfo(isShown: boolean): void {
    this.isInfoShown = isShown;
  }

  hideLoader(): void {
    this.isLoading = false;
  }

  set mapCenterCoordinates(coordinates: number[]) {
    this._mapCenterCoordinates = coordinates;
  }

  get mapCenterCoordinates(): number[] {
    return this._mapCenterCoordinates;
  }
}
